import React from 'react';
import { Loader2 } from 'lucide-react';
import { BasketHeader } from './BasketCard/BasketHeader';
import { TopicItem } from './BasketCard/TopicItem';

const MAX_NAME_LENGTH = 60;
const MAX_DETAIL_LENGTH = 80;

const PlaceholderItem = () => (
  <div className="flex flex-col items-center w-full h-full p-1 overflow-hidden rounded-md">
    <div className="w-full mb-1 flex justify-center">
      <span
        className="inline-block px-2 py-1 rounded-full font-medium text-xs text-neutral-500 border border-dashed border-neutral-400 text-center"
        style={{ lineHeight: '1.2' }}
      >
        future item
      </span>
    </div>
    <p className="w-full max-w-[90%] text-center text-xs italic text-neutral-400 leading-tight">
      hard detail
    </p>
  </div>
);

// Preview cell for the basket that would be created from the current BSI
// search query. Mirrors BasketCard geometry so it sits in the grid as a
// sibling; only the button is interactive.
const GhostBasketCard = ({ name, topic, onCreate, isCreating }) => {
  const displayName =
    name.length > MAX_NAME_LENGTH
      ? `${name.slice(0, MAX_NAME_LENGTH).trimEnd()}…`
      : name;

  // Carry the hard detail through so TopicItem renders it beneath the pill
  // exactly as real basket cards do, truncated to fit the cell.
  const hardDetail =
    topic.concepts?.find(concept => concept.is_highlighted) ||
    topic.concepts?.[0] ||
    null;
  const truncatedDetailText =
    hardDetail?.text && hardDetail.text.length > MAX_DETAIL_LENGTH
      ? `${hardDetail.text.slice(0, MAX_DETAIL_LENGTH).trimEnd()}…`
      : hardDetail?.text;

  const pillTopic = {
    id: topic.id,
    name: topic.name,
    tag: topic.tag || topic.tags,
    concepts: truncatedDetailText
      ? [{ id: 'ghost-hard-detail', text: truncatedDetailText, is_highlighted: true }]
      : [],
  };

  return (
    <div
      className="
        bg-white rounded-xl p-4 pt-2 w-full
        transition-all duration-300 relative
        border-2 border-dashed border-neutral-400
        shadow-sm
        h-[320px] flex flex-col
      "
    >
      <div className="flex justify-between items-start relative mb-0">
        <BasketHeader name={displayName} />
        <div className="ml-auto flex items-center py-1">
          <span className="text-sm font-medium text-cranberry-800">NEW</span>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 mt-0 flex-grow overflow-hidden">
        <TopicItem topic={pillTopic} />
        <PlaceholderItem />
      </div>

      <div className="mt-auto rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2">
        <p className="text-xs font-semibold text-neutral-500">Later, you'll be asked</p>
        <p className="text-sm text-neutral-900">
          Why is <strong>{topic.name}</strong> in the{' '}
          <strong className="bg-amber-100 rounded px-1">{displayName}</strong> basket?
        </p>
      </div>

      <div className="pt-2">
        <button
          type="button"
          onClick={onCreate}
          disabled={isCreating}
          aria-label={`Create basket ${name} and place topic ${topic.name} in it`}
          className="
            w-full px-4 py-2 bg-transparent
            border-2 border-black border-b-[3px] rounded-lg
            text-sm font-semibold text-black
            hover:bg-neutral-50 active:bg-neutral-100 transition-colors
            disabled:opacity-60 disabled:cursor-not-allowed
            touch-manipulation min-h-[44px]
            flex items-center justify-center gap-2
          "
        >
          {isCreating ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
              <span>Creating</span>
            </>
          ) : (
            <span>Create and place</span>
          )}
        </button>
      </div>
    </div>
  );
};

export default GhostBasketCard;
